"use strict";
/**
 * @fileoverview Cocos Creator 扩展配置生成器
 * @description 自动生成符合 Cocos Creator 标准的扩展配置，提供完整的集成支持
 *
 * @author ESEngine Team
 * @version 2.1.0
 * @since 2.1.0
 * @license MIT
 *
 * @example
 * ```typescript
 * const generator = new CocosExtensionGenerator();
 *
 * // 生成扩展配置
 * const config = generator.generateExtensionConfig(pluginInstance);
 *
 * // 写入配置文件
 * generator.writeExtensionFiles('./extensions/my-extension', config);
 * ```
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExtensionGenerator = exports.CocosExtensionGenerator = void 0;
const fs = __importStar(require("fs"));
const path = __importStar(require("path"));
/**
 * Cocos Creator 扩展配置生成器
 *
 * @description 自动生成符合 Cocos Creator 标准的扩展配置和文件结构
 * 支持以下功能：
 * - 自动生成 package.json 配置
 * - 创建标准的文件结构
 * - 生成面板和消息配置
 * - 支持菜单和快捷键集成
 * - 提供完整的开发工具支持
 *
 * @class CocosExtensionGenerator
 * @since 2.1.0
 *
 * @example
 * ```typescript
 * const generator = new CocosExtensionGenerator();
 *
 * // 从插件实例生成配置
 * const plugin = new MyPlugin();
 * const config = generator.generateFromPlugin(plugin);
 *
 * // 写入扩展文件
 * await generator.writeExtensionFiles('./extensions/my-extension', config);
 *
 * // 验证配置
 * const validation = generator.validateConfig(config);
 * if (validation.valid) {
 *   console.log('配置验证通过');
 * }
 * ```
 */
class CocosExtensionGenerator {
    /**
     * 构造函数
     *
     * @since 2.1.0
     */
    constructor() {
        this.TEMPLATE_VERSION = '2.1.0';
        this.DEFAULT_COCOS_VERSION = '>=3.3.0';
        // 初始化生成器
    }
    /**
     * 从插件实例生成扩展配置
     *
     * @param plugin 插件实例
     * @returns 生成的扩展配置
     *
     * @public
     * @since 2.1.0
     */
    generateFromPlugin(plugin) {
        const config = plugin.getConfig();
        return this.generateExtensionConfig(config);
    }
    /**
     * 生成完整的扩展配置
     *
     * @param config 插件配置
     * @returns 扩展配置对象
     *
     * @public
     * @since 2.1.0
     */
    generateExtensionConfig(config) {
        return {
            name: this.sanitizeName(config.name),
            version: config.version || '1.0.0',
            description: config.description || `${config.displayName || config.name} - Nexus Plugin`,
            author: config.author || 'Nexus Framework',
            main: config.main || './dist/main.js',
            contributions: {
                profile: this.generateProfileContributions(config),
                menu: this.generateMenuContributions(config),
                messages: this.generateMessageContributions(config),
                panels: this.generatePanelContributions(config),
                shortcuts: this.generateShortcutContributions(config)
            },
            engines: config.engines || {
                'cocos-creator': this.DEFAULT_COCOS_VERSION
            },
            dependencies: this.generateDependencies(config),
            nexus: {
                generated: true,
                timestamp: new Date().toISOString(),
                version: this.TEMPLATE_VERSION
            }
        };
    }
    /**
     * 生成扩展配置文件
     *
     * @param config 扩展配置
     * @returns JSON 字符串
     *
     * @public
     * @since 2.1.0
     */
    generatePackageJson(config) {
        const packageConfig = {
            name: config.name,
            version: config.version,
            description: config.description,
            author: config.author,
            main: config.main,
            contributions: config.contributions,
            engines: config.engines,
            dependencies: config.dependencies,
            nexus: config.nexus
        };
        return JSON.stringify(packageConfig, null, 2);
    }
    /**
     * 生成主入口文件
     *
     * @param config 扩展配置
     * @returns 主文件内容
     *
     * @public
     * @since 2.1.0
     */
    generateMainFile(config) {
        const panelMethods = this.generatePanelMethods(config);
        const messageMethods = this.generateMessageMethods(config);
        return `/**
 * ${config.description}
 * 
 * 由 Nexus Framework 自动生成
 * 生成时间: ${new Date().toISOString()}
 */

'use strict';

const { NexusExtension } = require('@esengine/cocos-nexus');

module.exports = {
    /**
     * 扩展加载时执行
     */
    load() {
        console.log('[${config.name}] 扩展加载');
        
        // 初始化 Nexus 扩展
        if (typeof NexusExtension !== 'undefined') {
            NexusExtension.initialize('${config.name}');
        }
    },

    /**
     * 扩展卸载时执行
     */
    unload() {
        console.log('[${config.name}] 扩展卸载');
        
        // 清理 Nexus 扩展
        if (typeof NexusExtension !== 'undefined') {
            NexusExtension.cleanup('${config.name}');
        }
    },

    /**
     * 消息处理器
     */
    messages: {
${messageMethods}
${panelMethods}
    }
};
`;
    }
    /**
     * 生成面板文件
     *
     * @param panelName 面板名称
     * @param panelConfig 面板配置
     * @returns 面板文件内容
     *
     * @public
     * @since 2.1.0
     */
    generatePanelFile(panelName, panelConfig) {
        return `/**
 * ${panelConfig.title} 面板
 * 
 * 由 Nexus Framework 自动生成
 */

'use strict';

const { NexusPanel } = require('@esengine/cocos-nexus');

module.exports = {
    /**
     * 面板模板
     */
    template: \`
        <div class="nexus-panel">
            <h1>${panelConfig.title}</h1>
            <div id="content">
                <!-- 面板内容将在这里动态生成 -->
            </div>
        </div>
    \`,

    /**
     * 面板样式
     */
    style: \`
        .nexus-panel {
            padding: 16px;
            height: 100%;
            display: flex;
            flex-direction: column;
        }
        
        .nexus-panel h1 {
            margin: 0 0 16px 0;
            font-size: 18px;
            font-weight: 600;
        }
        
        #content {
            flex: 1;
            overflow: auto;
        }
    \`,

    /**
     * 面板就绪时执行
     */
    ready() {
        console.log('[${panelName}] 面板就绪');
        
        // 初始化 Nexus 面板
        if (typeof NexusPanel !== 'undefined') {
            this.nexusPanel = new NexusPanel('${panelName}');
            this.nexusPanel.mount(this.$el.querySelector('#content'));
        }
    },

    /**
     * 面板显示时执行
     */
    show() {
        console.log('[${panelName}] 面板显示');
        
        if (this.nexusPanel) {
            this.nexusPanel.show();
        }
    },

    /**
     * 面板隐藏时执行
     */
    hide() {
        console.log('[${panelName}] 面板隐藏');
        
        if (this.nexusPanel) {
            this.nexusPanel.hide();
        }
    },

    /**
     * 面板关闭时执行
     */
    close() {
        console.log('[${panelName}] 面板关闭');
        
        if (this.nexusPanel) {
            this.nexusPanel.destroy();
        }
    }
};
`;
    }
    /**
     * 写入扩展文件到指定目录
     *
     * @param outputDir 输出目录
     * @param config 扩展配置
     * @returns 写入结果
     *
     * @public
     * @since 2.1.0
     */
    async writeExtensionFiles(outputDir, config) {
        const result = {
            success: true,
            files: [],
            errors: []
        };
        try {
            // 确保输出目录存在
            if (!fs.existsSync(outputDir)) {
                fs.mkdirSync(outputDir, { recursive: true });
            }
            // 写入 package.json
            const packagePath = path.join(outputDir, 'package.json');
            const packageContent = this.generatePackageJson(config);
            fs.writeFileSync(packagePath, packageContent, 'utf-8');
            result.files.push(packagePath);
            // 写入主文件
            const mainPath = path.join(outputDir, 'main.js');
            const mainContent = this.generateMainFile(config);
            fs.writeFileSync(mainPath, mainContent, 'utf-8');
            result.files.push(mainPath);
            // 创建面板目录和文件
            if (config.contributions.panels) {
                const panelsDir = path.join(outputDir, 'panels');
                if (!fs.existsSync(panelsDir)) {
                    fs.mkdirSync(panelsDir, { recursive: true });
                }
                for (const [panelName, panelConfig] of Object.entries(config.contributions.panels)) {
                    const panelPath = path.join(panelsDir, `${panelName}.js`);
                    const panelContent = this.generatePanelFile(panelName, panelConfig);
                    fs.writeFileSync(panelPath, panelContent, 'utf-8');
                    result.files.push(panelPath);
                }
            }
            // 创建静态资源目录
            const staticDir = path.join(outputDir, 'static');
            if (!fs.existsSync(staticDir)) {
                fs.mkdirSync(staticDir, { recursive: true });
            }
            // 创建默认图标
            const iconPath = path.join(staticDir, 'icon.png');
            if (!fs.existsSync(iconPath)) {
                this.createDefaultIcon(iconPath);
                result.files.push(iconPath);
            }
            // 创建 README
            const readmePath = path.join(outputDir, 'README.md');
            const readmeContent = this.generateReadme(config);
            fs.writeFileSync(readmePath, readmeContent, 'utf-8');
            result.files.push(readmePath);
        }
        catch (error) {
            result.success = false;
            result.errors.push(`写入文件失败: ${error}`);
        }
        return result;
    }
    /**
     * 验证扩展配置
     *
     * @param config 扩展配置
     * @returns 验证结果
     *
     * @public
     * @since 2.1.0
     */
    validateConfig(config) {
        const result = {
            valid: true,
            errors: [],
            warnings: []
        };
        // 检查必需字段
        if (!config.name) {
            result.errors.push('缺少必需字段: name');
        }
        if (!config.version) {
            result.errors.push('缺少必需字段: version');
        }
        if (!config.main) {
            result.errors.push('缺少必需字段: main');
        }
        // 检查名称格式
        if (config.name && !/^[a-z][a-z0-9-]*$/.test(config.name)) {
            result.errors.push('扩展名称格式不正确，应为小写字母开头，只包含小写字母、数字和连字符');
        }
        // 检查版本格式
        if (config.version && !/^\d+\.\d+\.\d+/.test(config.version)) {
            result.warnings.push('版本号格式建议使用语义化版本（如 1.0.0）');
        }
        // 检查贡献配置
        if (!config.contributions || Object.keys(config.contributions).length === 0) {
            result.warnings.push('未定义任何贡献，扩展可能无法正常工作');
        }
        result.valid = result.errors.length === 0;
        return result;
    }
    // ========== 私有方法 ==========
    /**
     * 规范化扩展名称
     *
     * @private
     * @param name 原始名称
     * @returns 规范化后的名称
     */
    sanitizeName(name) {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/^-+|-+$/g, '')
            .replace(/-+/g, '-');
    }
    /**
     * 生成Profile贡献
     *
     * @private
     * @param config 插件配置
     * @returns Profile贡献配置
     */
    generateProfileContributions(config) {
        return {
            editor: {
                label: config.displayName || config.name,
                icon: config.icon || './static/icon.png'
            }
        };
    }
    /**
     * 生成菜单贡献
     *
     * @private
     * @param config 插件配置
     * @returns 菜单贡献配置
     */
    generateMenuContributions(config) {
        const menus = [];
        // 自动生成面板菜单
        if (config.panels) {
            Object.entries(config.panels).forEach(([panelName, panelConfig], index) => {
                menus.push({
                    path: `扩展/${config.displayName || config.name}`,
                    label: `打开${panelConfig.title}`,
                    message: `open-${panelName}`,
                    order: index
                });
            });
        }
        // 添加用户自定义菜单
        if (config.menu) {
            menus.push(...config.menu);
        }
        return menus;
    }
    /**
     * 生成消息贡献
     *
     * @private
     * @param config 插件配置
     * @returns 消息贡献配置
     */
    generateMessageContributions(config) {
        var _a;
        const messages = {};
        // 自动生成面板消息
        if (config.panels) {
            Object.keys(config.panels).forEach(panelName => {
                messages[`open-${panelName}`] = {
                    methods: [`open${this.capitalize(panelName)}`],
                    public: true,
                    doc: `打开${panelName}面板`
                };
            });
        }
        // 添加用户自定义消息
        if ((_a = config.contributions) === null || _a === void 0 ? void 0 : _a.messages) {
            Object.assign(messages, config.contributions.messages);
        }
        return messages;
    }
    /**
     * 生成面板贡献
     *
     * @private
     * @param config 插件配置
     * @returns 面板贡献配置
     */
    generatePanelContributions(config) {
        const panels = {};
        if (config.panels) {
            Object.entries(config.panels).forEach(([panelName, panelConfig]) => {
                panels[panelName] = {
                    title: panelConfig.title,
                    type: panelConfig.type || (panelConfig.dockable !== false ? 'dockable' : 'simple'),
                    main: panelConfig.main || `./${panelName}.js`,
                    icon: panelConfig.icon || './static/icon.png',
                    flags: {
                        resizable: panelConfig.resizable !== false,
                        save_layout: true
                    },
                    size: {
                        'min-width': panelConfig.minWidth || 300,
                        'min-height': panelConfig.minHeight || 200,
                        width: panelConfig.width || 800,
                        height: panelConfig.height || 600
                    }
                };
            });
        }
        return panels;
    }
    /**
     * 生成快捷键贡献
     *
     * @private
     * @param config 插件配置
     * @returns 快捷键贡献配置
     */
    generateShortcutContributions(config) {
        return config.shortcuts || [];
    }
    /**
     * 生成依赖项
     *
     * @private
     * @param config 插件配置
     * @returns 依赖项配置
     */
    generateDependencies(config) {
        const dependencies = {};
        if (config.dependencies) {
            config.dependencies.forEach(dep => {
                dependencies[dep] = 'latest';
            });
        }
        return dependencies;
    }
    /**
     * 生成面板方法
     *
     * @private
     * @param config 扩展配置
     * @returns 面板方法代码
     */
    generatePanelMethods(config) {
        if (!config.contributions.panels) {
            return '';
        }
        const methods = [];
        Object.keys(config.contributions.panels).forEach(panelName => {
            methods.push(`        /**
         * 打开${this.capitalize(panelName)}面板
         */
        'open-${panelName}'() {
            Editor.Panel.open('${config.name}.${panelName}');
        }`);
        });
        return methods.join(',\n\n');
    }
    /**
     * 生成消息方法
     *
     * @private
     * @param config 扩展配置
     * @returns 消息方法代码
     */
    generateMessageMethods(config) {
        if (!config.contributions.messages) {
            return '';
        }
        const methods = [];
        Object.entries(config.contributions.messages).forEach(([messageName, messageConfig]) => {
            if (!messageName.startsWith('open-')) {
                methods.push(`        /**
         * ${messageConfig.doc || messageName}
         */
        '${messageName}'(event, ...args) {
            console.log('[${config.name}] 处理消息: ${messageName}', args);
            // TODO: 实现消息处理逻辑
        }`);
            }
        });
        return methods.length > 0 ? methods.join(',\n\n') + (config.contributions.panels ? ',\n\n' : '') : '';
    }
    /**
     * 生成 README 内容
     *
     * @private
     * @param config 扩展配置
     * @returns README 内容
     */
    generateReadme(config) {
        var _a;
        return `# ${config.name}

${config.description}

## 功能特性

${config.contributions.panels ? `### 面板
${Object.entries(config.contributions.panels).map(([name, panel]) => `- **${panel.title}**: ${panel.type === 'dockable' ? '可停靠面板' : '简单面板'}`).join('\n')}` : ''}

${config.contributions.menu && config.contributions.menu.length > 0 ? `### 菜单
${config.contributions.menu.map(menu => `- ${menu.label}`).join('\n')}` : ''}

## 安装

1. 将此扩展复制到 Cocos Creator 的 extensions 目录
2. 重启 Cocos Creator
3. 在扩展管理器中启用此扩展

## 开发

此扩展由 [Nexus Framework](https://github.com/esengine/cocos-nexus) 生成。

### 要求

- Cocos Creator ${config.engines['cocos-creator']}
- Nexus Framework ${((_a = config.nexus) === null || _a === void 0 ? void 0 : _a.version) || 'latest'}

### 构建

\`\`\`bash
npm install
npm run build
\`\`\`

## 许可证

MIT License
`;
    }
    /**
     * 创建默认图标
     *
     * @private
     * @param iconPath 图标路径
     */
    createDefaultIcon(iconPath) {
        // 创建一个简单的 SVG 图标，然后转换为 PNG
        // 这里可以实现图标生成逻辑
        const iconContent = ''; // 简化实现，实际应生成图标文件
        if (iconContent) {
            fs.writeFileSync(iconPath, iconContent);
        }
    }
    /**
     * 首字母大写
     *
     * @private
     * @param str 字符串
     * @returns 首字母大写的字符串
     */
    capitalize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
exports.CocosExtensionGenerator = CocosExtensionGenerator;
// 便捷导出
exports.ExtensionGenerator = new CocosExtensionGenerator();
